import {
  BENEFICIARY_INSIGHT_DISPLAY_TYPES,
  BeneficiaryFiltersDTO,
  BeneficiaryInsightDimensionsDTO,
  BeneficiaryInsightMetricsDTO,
  BeneficiaryInsightWidgetDTO,
} from '@repo/shared/dtos/beneficiary'
import { Form as UIForm } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { z } from '@repo/ui/lang'
import { isEqual } from 'lodash'
import { Flame } from 'lucide-react'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { getCurrentOrganizationStructuredDataSchema } from '@/store/user'

import DimensionsForm from './DimensionsForm'
import FiltersForm from './FiltersForm'
import GeneralInformationForm from './GeneralInformationForm'
import { buildDefaultValues } from './helpers'
import MetricForm from './MetricForm'
import { FormHandle, FormType, InsightsWidgetForm } from './types'

type EditFormProps = {
  widget: BeneficiaryInsightWidgetDTO | null
  onSubmit: (values: InsightsWidgetForm) => void
  onValidityChange?: (isValid: boolean) => void
}

const Form = forwardRef<FormHandle, EditFormProps>(
  ({ widget, onSubmit, onValidityChange }, ref) => {
    const { t } = useT({ keyPrefix: 'admin.insights.form' })
    const structuredDataSchema = useSelector(getCurrentOrganizationStructuredDataSchema)
    const [showJsonDisplay, setShowJsonDisplay] = useState(false)
    const defaultValues = useMemo(() => buildDefaultValues(widget, structuredDataSchema!), [widget])
    const { form, submit, isFormValid, submitOnPressMetaEnter } = useFormValidator({
      defaultValues,
      onSubmit: (values) => {
        onSubmit({
          title: values.title,
          description: values.description,
          type: values.type,
          filters: values.filters as BeneficiaryFiltersDTO,
          metrics: [values.metric] as BeneficiaryInsightMetricsDTO,
          dimensions: values.dimensions as BeneficiaryInsightDimensionsDTO,
        })
      },
      schema: z.object({
        title: z.string().min(1),
        description: z.string().min(1),
        type: z.enum(BENEFICIARY_INSIGHT_DISPLAY_TYPES),
        filters: z.array(z.unknown()),
        metric: z.unknown(),
        dimensions: z.array(z.unknown()),
        dimensionModes: z.array(
          z.object({
            date: z.enum(['simple', 'range', 'timeseries']).nullable(),
            number: z.enum(['simple', 'range']).nullable(),
          }),
        ),
      }).shape,
      customValidation: (values) => {
        const errors: Record<string, string> = {}
        // Pie chart requires at least one dimension
        if (
          (values.type === 'pie' || values.type === 'bar') &&
          (values.dimensions.length === 0 || values.dimensions[0] == null)
        ) {
          errors.dimensions = t('dimensions.pieChartRequiresDimension')
        }
        return errors
      },
    })

    const isChart = form.getValues('type') !== 'kpi'

    // Watch form values to detect changes from default values
    const currentValues = form.watch()
    const hasChanges = useMemo(() => {
      // Only check for changes if we have both current values and default values
      if (!currentValues || !defaultValues) return false
      return !isEqual(currentValues, defaultValues)
    }, [currentValues, defaultValues])

    // Combine form validity with change detection
    const isFormValidWithChanges = isFormValid && hasChanges

    useImperativeHandle(ref, () => ({
      submit: () => void submit(undefined),
      reset: () => form.reset(defaultValues),
    }))

    useEffect(() => {
      onValidityChange?.(isFormValidWithChanges)
    }, [isFormValidWithChanges, onValidityChange])

    return (
      <UIForm {...form}>
        <form className="space-y-4" onKeyDown={submitOnPressMetaEnter} onSubmit={submit}>
          <GeneralInformationForm form={form as FormType} />
          <FiltersForm form={form as FormType} />
          {isChart ? (
            <DimensionsForm form={form as FormType} />
          ) : (
            <MetricForm form={form as FormType} />
          )}
        </form>
        <button
          onClick={() => setShowJsonDisplay(!showJsonDisplay)}
          className="flex flex-shrink-0 items-center gap-1 text-xs underline"
        >
          <Flame className="size-4 text-red-500" />
          {t(showJsonDisplay ? 'hideJsonButton' : 'showJsonButton')}
        </button>
        {showJsonDisplay && (
          <pre className="whitespace-pre-wrap bg-gray-100 p-4 rounded-md">
            {JSON.stringify(form.getValues(), null, 2)}
          </pre>
        )}
      </UIForm>
    )
  },
)

export default Form
